/**
 * The disciplinary-hearing vocabularies: the five stages, the three document batches, and
 * the transitions the system is allowed to make between document states.
 *
 * Authority: `docs/specs/control-centre/06-hearings.md` §6 (the five stages and their
 * labels and tooltips, verbatim), §8 (the stepper's per-stage step states), §9 (the three
 * batches, their chips and their gate messages), §7 (the level ladder and the minimum
 * penalties), §18 (the `Gated` / `Draft` / `Issued / Filed` / `Not Required` states), and
 * `10-data-model.md` §7 with migration `007-hearings.sql` (the tables these words are
 * stored in — and the one word that is NOT stored).
 *
 * It lives in `packages/shared` for the reason `report-stages.ts` gives for itself: the API
 * decides which transitions it will accept and the Hearings page decides which buttons it
 * will draw, and those two answers must be the same answer. A screen that offers a
 * transition the route refuses is a lie told in a button. Migration 025 states the same
 * lattice a third time, in plpgsql, as the backstop under both.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE STAGE IS DERIVED AND IS NEVER STORED
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Spec §6, and the header of migration `007-hearings.sql`, both say it in the same words:
 *
 * > Stage is computed from the document pipeline … **Never store the stage** — it cannot
 * > then drift from the documents.
 *
 * So there is no `stage` column, there is no `stage` field on any repository input, and
 * {@link deriveHearingStage} is the only place the word is decided. Everything a screen or
 * a figure strip wants to know about where a case has got to is a question about its three
 * `hearing_document` rows, asked here.
 *
 * The temptation to store it is real — a stored stage is one column to filter on instead of
 * a join, and the register filters on stage. It is refused because the failure mode is
 * silent: a decision letter reopened for amendment moves a document and, with a stored
 * stage, leaves the register saying `Decision Issued` about a case that no longer has one.
 * Deriving costs a projection; storing costs the truth of the register.
 *
 * British English throughout, per the standing copy rule.
 */
/** The three batches, in order. */
export const HEARING_BATCHES = [1, 2, 3];
/** The stage chip labels, verbatim from §6. */
export const HEARING_STAGE_LABELS = {
    created: "Created · Letter Pending",
    letter: "Letter Issued",
    minutes: "Minutes Filed",
    decision: "Decision Issued",
    closed: "Closed",
};
/** The stage chip tooltips, verbatim from §6. */
export const HEARING_STAGE_TOOLTIPS = {
    created: "Case created by the TD — Hearing Letter not yet issued",
    letter: "Hearing Letter issued to the Team Manager — hearing to come",
    minutes: "Hearing held and minutes filed — written decision outstanding",
    decision: "Decision letter issued to the Team Manager and the person involved",
    closed: "Closed without a hearing — informal enquiry or no action",
};
/** The batch titles and subtitles of §9's card table, verbatim. */
export const HEARING_BATCH_TITLES = {
    1: "Hearing Letter",
    2: "Meeting Minutes",
    3: "Decision Letter",
};
export const HEARING_BATCH_SUBTITLES = {
    1: "To the Team Manager",
    2: "The record, kept for any appeal",
    3: "To the Team Manager and the person involved, cc the Competition Office",
};
/**
 * The state chip a batch card shows, §9.
 *
 * Batch 2's `pending` reads `Pending` on the card and `Not Yet Filed` in the workspace bar;
 * they are the same state seen from two places, which is why the workspace spelling is a
 * separate map rather than a second state.
 */
export const HEARING_DOCUMENT_STATE_LABELS = {
    pending: "Pending",
    draft: "Draft",
    issued: "Issued",
    filed: "Filed",
    not_required: "Not Required",
};
/** §11's workspace-bar chip for the minutes: `pending` is spelled `Not Yet Filed` there. */
export const HEARING_MINUTES_WORKSPACE_LABELS = {
    ...HEARING_DOCUMENT_STATE_LABELS,
    pending: "Not Yet Filed",
};
/**
 * The transitions **this system** may make, per batch, by the state the document is on now.
 *
 * Read the three rows as three sentences from §9 through §12:
 *
 *  * **Batch 1, the Hearing Letter.** `pending → draft` is `Draft Letter`; `draft → issued`
 *    is `Print`, which files the `.doc` and sends the PDF; `issued → draft` is
 *    `Reopen For Editing`, which §10 offers in as many words — *"reopen it above to amend
 *    and reprint"* — so `issued` is emphatically NOT terminal.
 *  * **Batch 2, the Minutes.** `pending → filed` is `Submit`. There is no `draft` in
 *    between because §9's chip vocabulary for this card is `Pending / Filed / Not Required`
 *    and nothing else: the form is either open in front of the secretary or filed to the
 *    case. `filed → pending` is `Reopen Form`, whose own chip §11 spells `Not Yet Filed` —
 *    which is this state, under the name that card gives it.
 *  * **Batch 3, the Decision Letter.** `pending → draft` is `Draft Decision`;
 *    `draft → issued` is `Save`; `issued → draft` is `Reopen For Editing`. §12's reversed
 *    decision runs through here — the TD reopens the MINUTES, resubmits them, and drafts a
 *    revised decision letter — which is why this row needs no separate "revise" state.
 *
 * `not_required` is reachable only from `pending`, on every batch, and is terminal. That is
 * §18's `Not Required` row — *"a closed case shows `Not Required` across all three batches
 * with no action buttons"* — and closing is a decision taken at the `created` stage, before
 * any document has been drafted. A case whose letter has gone out is not closable as an
 * informal enquiry; it is decided, or it is decided to be no breach.
 */
export const HEARING_DOCUMENT_TRANSITIONS = {
    1: {
        pending: ["draft", "not_required"],
        draft: ["issued"],
        issued: ["draft"],
        filed: [],
        not_required: [],
    },
    2: {
        pending: ["filed", "not_required"],
        draft: [],
        issued: [],
        filed: ["pending"],
        not_required: [],
    },
    3: {
        pending: ["draft", "not_required"],
        draft: ["issued"],
        issued: ["draft"],
        filed: [],
        not_required: [],
    },
};
/** Every document is born `pending` — §14's *"all documents `Pending`"* on case creation. */
export const HEARING_DOCUMENT_INITIAL_STATE = "pending";
/** True when the state admits no further move at all. Only `not_required` does not. */
export function isHearingDocumentTerminal(batch, state) {
    return HEARING_DOCUMENT_TRANSITIONS[batch][state].length === 0;
}
/** True when `from → to` is a move this system makes on this batch. */
export function isHearingDocumentTransitionLegal(batch, from, to) {
    return HEARING_DOCUMENT_TRANSITIONS[batch][from].includes(to);
}
/**
 * The stage, computed. §6: *"`closed` → `decision` (decision Issued) → `minutes` (minutes
 * Filed) → `letter` (letter Issued) → else `created`"*, in that order of precedence.
 *
 * `closed` is read off batch 1 rather than off a flag on the case: §18 says a closed case
 * shows `Not Required` across all three batches, and a letter that was never required is
 * the fact that distinguishes an informal enquiry from a case that simply has not got there
 * yet. Deriving it from the document keeps the promise of this file whole — there is no
 * second source for the stage, not even a boolean.
 *
 * The order matters and is not alphabetical, cosmetic or arbitrary. A decision letter that
 * has been reopened for amendment sits at `draft`, so the case falls back to `minutes` and
 * the register says `Decision Outstanding` — which is true while the amendment is being
 * written, and is exactly what a stored stage would have got wrong.
 */
export function deriveHearingStage(documents) {
    if (documents[1] === "not_required")
        return "closed";
    if (documents[3] === "issued")
        return "decision";
    if (documents[2] === "filed")
        return "minutes";
    if (documents[1] === "issued")
        return "letter";
    return "created";
}
/**
 * §4's `Open` figure: *"Letter, hearing or decision outstanding"* — stage ∈ created /
 * letter / minutes. A closed case is not open, and a decided one is not either.
 */
export function isHearingOpen(stage) {
    return stage === "created" || stage === "letter" || stage === "minutes";
}
/**
 * §9's gate: batch 2 becomes available once the Hearing Letter has been issued, and batch 3
 * once the minutes have been filed. Returns `null` when the batch is available, and the
 * gate message — verbatim from §9's table — when it is not.
 *
 * **Closing a case is not gated**, and that is the one asymmetry here. Marking every batch
 * `not_required` is how §18's closed case is expressed, and it happens precisely when no
 * letter has been issued, so a gate that applied to it would make closing impossible. The
 * gate governs the pipeline, not the exit from it; this function therefore takes the
 * destination state and waves `not_required` through.
 *
 * **The gate is on LEAVING `pending`, and on nothing else** — it is the §9 card's "this
 * button is not available yet", not a standing invariant over the three rows. §10 lets the
 * TD reopen an issued Hearing Letter *"to amend and reprint"* long after the minutes are
 * filed, and a gate read as an invariant would refuse that: batch 1 would be back at
 * `draft` while batch 2 sat at `filed`, and the next legitimate move on either would be
 * blocked by a condition that describes a case which has moved ON, not one which has not
 * moved yet. Migration 025's trigger is written to the same rule, in the same words.
 */
export function hearingBatchGate(batch, from, to, documents) {
    if (to === "not_required")
        return null;
    if (from !== "pending")
        return null;
    if (batch === 2 && documents[1] !== "issued") {
        return "Available once the Hearing Letter has been issued.";
    }
    if (batch === 3 && documents[2] !== "filed") {
        return "Available once the minutes have been filed.";
    }
    return null;
}
/**
 * The Code of Conduct levels of §7, and the minimum penalty each carries.
 *
 * The strings are the level-chip tooltip, rendered `title="Minimum penalty: …"`, and they
 * are also printed in the charge box of step card 01. They are constants HERE and article
 * text is NOT: §2's *"versioning is mandatory"* binds the article LETTERING and WORDING to
 * the Code edition in force, which is `bound_instrument` data and must never become a
 * hard-coded list. The three ladder rungs are the part that does not shift between
 * editions, which is why these three sentences may live in code and the catalogue may not.
 */
export const HEARING_LEVEL_MINIMUM_PENALTY = {
    1: "Official reprimand, or a suspension of at least one match",
    2: "Suspension of at least two matches",
    3: "Suspension of at least five matches",
};
/**
 * §7: *"Case level = the maximum level across all charges on the case."* Derived at render
 * time, like the stage, and for the same reason — a charge added or removed must move it.
 *
 * Returns `null` for a case with no charges, which the create path forbids (§14: *"Charge 1
 * is required"*) but a projection must still be able to describe without inventing a level.
 */
export function deriveHearingCaseLevel(levels) {
    if (levels.length === 0)
        return null;
    return levels.reduce((max, level) => (level > max ? level : max), 1);
}
/** §5's five stage-key toggle buttons, in register order. */
export const HEARING_STAGES = [
    "created",
    "letter",
    "minutes",
    "decision",
    "closed",
];
//# sourceMappingURL=hearings.js.map